import { useEffect, useRef } from 'react';
import { detectPlatform } from './platform';
import type { AppName } from './types';

export interface AccountAttributionClient {
  rpc(name: string, args: Record<string, string>): PromiseLike<{ error: unknown }>;
}

/** The server checks the account age and accepts the first app claim only. */
export function useAccountCreationAttribution(
  client: AccountAttributionClient,
  userId: string | undefined,
  app: AppName | 'montessori',
  createdAt?: string,
) {
  const clientRef = useRef(client);
  useEffect(() => { clientRef.current = client; }, [client]);
  useEffect(() => {
    if (!userId) return;
    const createdAtMs = createdAt ? Date.parse(createdAt) : NaN;
    // This saves a request for old accounts. The server remains the authority.
    if (Number.isFinite(createdAtMs) && Date.now() - createdAtMs > 10 * 60_000) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const claim = async () => {
      attempts += 1;
      try {
        const { error } = await clientRef.current.rpc('attribute_account_creation', {
          p_app: app,
          p_platform: detectPlatform(),
        });
        if (!error) return;
      } catch {
        // Auth must remain usable if attribution cannot reach the database.
      }
      if (!cancelled && attempts < 6) timer = setTimeout(claim, 5000);
    };
    void claim();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [userId, app, createdAt]);
}
